import styled from 'styled-components'
import { vimovaTheme } from '../../theme/vimovaTheme'

/* ── Table of Contents ── */

export const TOCSection = styled.div`
  margin-top: 20px;

  @media print {
    margin-top: 20px !important;
  }
`

export const TOCTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 8px 0;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.small} !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TOCList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: toc-counter;

  @media print {
    list-style: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }
`

export const TOCItem = styled.li`
  counter-increment: toc-counter;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  color: ${vimovaTheme.colors.text};
  padding: 4px 0 4px 16px;

  &::before {
    content: counter(toc-counter) '. ';
  }

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.text} !important;
    padding: 4px 0 4px 16px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Document Preview Cards ── */

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 20px;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr !important;
    gap: 16px !important;
    margin-top: 20px !important;
  }
`

export const DocumentCard = styled.div`
  background: ${vimovaTheme.colors.backgroundLight};
  border-radius: ${vimovaTheme.borderRadius.signature};
  padding: 16px;
  display: flex;
  flex-direction: column;

  @media print {
    background: ${vimovaTheme.colors.backgroundLight} !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    padding: 16px !important;
    display: flex !important;
    flex-direction: column !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DocumentCardTitle = styled.h4`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 12px 0;
  min-height: 32px;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.small} !important;
    color: ${vimovaTheme.colors.primary} !important;
    min-height: 32px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DocumentPreviewArea = styled.div`
  background: ${vimovaTheme.colors.backgroundMuted};
  border-radius: ${vimovaTheme.borderRadius.signature};
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media print {
    background: ${vimovaTheme.colors.backgroundMuted} !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    height: 180px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DocumentPreviewImage = styled.img`
  max-width: 100%;
  max-height: 160px;
  object-fit: contain;
  border-radius: ${vimovaTheme.borderRadius.small};

  @media print {
    max-width: 100% !important;
    max-height: 160px !important;
    object-fit: contain !important;
  }
`

export const DocumentPlaceholderLines = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 0;

  @media print {
    width: 80% !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 6px !important;
    padding: 16px 0 !important;
  }
`

export const PlaceholderLine = styled.div<{ $width?: string }>`
  height: 3px;
  border-radius: 1.5px;
  background: #d4cfc6;
  width: ${({ $width }) => $width || '100%'};

  @media print {
    height: 3px !important;
    border-radius: 1.5px !important;
    background: #d4cfc6 !important;
    width: ${({ $width }) => $width || '100%'} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DocumentTypeLabel = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 8px;
  color: ${vimovaTheme.colors.textMuted};
  margin-top: 8px;

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    margin-top: 8px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DocumentDescription = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 8px;
  color: ${vimovaTheme.colors.textLight};
  margin-top: auto;
  padding-top: 8px;

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.textLight} !important;
    margin-top: auto !important;
    padding-top: 8px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Photo Grid (inside a card) ── */

export const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 8px;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
    padding: 8px !important;
  }
`

export const PhotoThumbnail = styled.div`
  background: ${vimovaTheme.colors.backgroundMuted};
  border-radius: ${vimovaTheme.borderRadius.small};
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media print {
    background: ${vimovaTheme.colors.backgroundMuted} !important;
    border-radius: ${vimovaTheme.borderRadius.small} !important;
    height: 56px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PhotoLabel = styled.span`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 8px;
  color: ${vimovaTheme.colors.textMuted};

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Glossary Section ── */

export const GlossarySection = styled.div`
  margin-top: 24px;

  @media print {
    margin-top: 24px !important;
  }
`

export const GlossaryTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.black};
  font-size: 12px;
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 12px 0;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.black} !important;
    font-size: 12px !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const GlossaryItem = styled.div`
  & + & {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${vimovaTheme.colors.borderLight};
  }

  @media print {
    & + & {
      margin-top: 8px !important;
      padding-top: 8px !important;
      border-top: 1px solid ${vimovaTheme.colors.borderLight} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const GlossaryTerm = styled.span`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 10px;
  color: ${vimovaTheme.colors.text};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 10px !important;
    color: ${vimovaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const GlossaryDefinition = styled.p`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.micro};
  color: ${vimovaTheme.colors.textLight};
  margin: 2px 0 0 0;
  line-height: 1.5;

  @media print {
    font-size: ${vimovaTheme.fontSizes.micro} !important;
    color: ${vimovaTheme.colors.textLight} !important;
    line-height: 1.5 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Data Sources & Limitations ── */

export const InfoSection = styled.div`
  margin-top: 20px;

  @media print {
    margin-top: 20px !important;
  }
`

export const InfoSectionTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.black};
  font-size: 12px;
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 8px 0;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.black} !important;
    font-size: 12px !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const InfoList = styled.ul`
  list-style: disc;
  padding-left: 20px;
  margin: 0;

  @media print {
    list-style: disc !important;
    padding-left: 20px !important;
    margin: 0 !important;
  }
`

export const InfoListItem = styled.li`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.micro};
  color: ${vimovaTheme.colors.textLight};
  line-height: 1.6;

  @media print {
    font-size: ${vimovaTheme.fontSizes.micro} !important;
    color: ${vimovaTheme.colors.textLight} !important;
    line-height: 1.6 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const MethodologyText = styled.p`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.micro};
  color: ${vimovaTheme.colors.textLight};
  line-height: 1.6;
  margin: 0;

  @media print {
    font-size: ${vimovaTheme.fontSizes.micro} !important;
    color: ${vimovaTheme.colors.textLight} !important;
    line-height: 1.6 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Full-page file display (additional pages) ── */

export const FilePageTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 13px;
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 12px 0;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 13px !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const FilePageImage = styled.img`
  max-width: 100%;
  max-height: calc(100% - 40px);
  object-fit: contain;
  border-radius: ${vimovaTheme.borderRadius.signature};

  @media print {
    max-width: 100% !important;
    max-height: calc(100% - 40px) !important;
    object-fit: contain !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
  }
`

export const PdfPageImage = styled.img`
  width: 100%;
  height: auto;
  display: block;

  @media print {
    width: 100% !important;
    height: auto !important;
  }
`

export const PdfPageLabel = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 8px;
  color: ${vimovaTheme.colors.textMuted};
  text-align: center;
  margin-bottom: 6px;

  @media print {
    font-size: 8px !important;
    color: ${vimovaTheme.colors.textMuted} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/* ── Upload / Interactive elements ── */

export const AddFileButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 16px;
  background: ${vimovaTheme.colors.primary};
  color: ${vimovaTheme.colors.white};
  border: none;
  border-radius: 20px;
  font-family: ${vimovaTheme.fonts.heading};
  font-size: 11px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  @media print {
    display: none !important;
  }
`

export const UploadInput = styled.input`
  display: none;
`

export const DocumentCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`

export const DeleteFileButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${vimovaTheme.colors.textMuted};
  flex-shrink: 0;
  border-radius: 4px;
  transition:
    color 0.2s,
    background 0.2s;

  &:hover {
    color: #ef4444;
    background: #fef2f2;
  }

  @media print {
    display: none !important;
  }
`

export const NoFilesMessage = styled.div`
  margin-top: 20px;
  padding: 24px;
  text-align: center;
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.textMuted};
  background: ${vimovaTheme.colors.backgroundLight};
  border-radius: ${vimovaTheme.borderRadius.signature};

  @media print {
    display: none !important;
  }
`

/* ── Upload Form Modal ── */

export const UploadFormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  @media print {
    display: none !important;
  }
`

export const UploadFormContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`

export const UploadFormTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 16px;
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 16px 0;
`

export const UploadFormGroup = styled.div`
  margin-bottom: 14px;
`

export const UploadFormLabel = styled.label`
  display: block;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 12px;
  font-weight: 500;
  color: ${vimovaTheme.colors.text};
  margin-bottom: 4px;
`

export const UploadFormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${vimovaTheme.colors.border};
  border-radius: 8px;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 13px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${vimovaTheme.colors.primary};
  }
`

export const UploadFormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${vimovaTheme.colors.border};
  border-radius: 8px;
  font-family: ${vimovaTheme.fonts.body};
  font-size: 13px;
  min-height: 60px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${vimovaTheme.colors.primary};
  }
`

export const UploadFormButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`

export const UploadFormButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 20px;
  border-radius: 20px;
  font-family: ${vimovaTheme.fonts.heading};
  font-size: 12px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  cursor: pointer;
  transition: opacity 0.2s;
  border: ${({ $primary }) =>
    $primary ? 'none' : `1px solid ${vimovaTheme.colors.border}`};
  background: ${({ $primary }) =>
    $primary ? vimovaTheme.colors.primary : 'white'};
  color: ${({ $primary }) =>
    $primary ? vimovaTheme.colors.white : vimovaTheme.colors.text};

  &:hover {
    opacity: 0.85;
  }
`

export const UploadFormPreview = styled.img`
  max-width: 100%;
  max-height: 150px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 12px;
`
